/**
 * Monaco is loaded as a global AMD bundle from `assets/monaco/min/vs`. When the
 * Designer runs standalone that path resolves against its own document, but when
 * it is federation-embedded the document belongs to the HOST, which has no Monaco
 * assets — the loader (and worker) must instead resolve against the REMOTE bundle
 * origin (same class of problem as the image/CSS url rewrites in main.ts).
 *
 * main.ts computes the absolute base from `import.meta.url` once at bootstrap and
 * stashes it here; the lazy feature modules read it back when configuring
 * `MonacoEditorModule.forRoot`. `import.meta` stays confined to main.ts so files
 * imported by Jest specs never touch it.
 */

import { StaticProvider } from '@angular/core';
import { NGX_MONACO_EDITOR_CONFIG, NgxMonacoEditorConfig } from 'ngx-monaco-editor-v2';
import { MonacoThemeSync } from './monaco-theme';

export const MONACO_VS_RELATIVE_BASE = 'assets/monaco/min/vs';

let vsBase: string | undefined;

export function resolveMonacoVsBase(bundleUrl: string): string {
  return new URL(MONACO_VS_RELATIVE_BASE, bundleUrl).href;
}

export function setMonacoVsBase(base: string | undefined): void {
  vsBase = base;
}

export function getMonacoVsBase(): string {
  return vsBase ?? MONACO_VS_RELATIVE_BASE;
}

/**
 * AMD-shaped `require` shim backed by the BUNDLED ESM monaco.
 *
 * The embedding host's CSP (`script-src` without the remote origin) blocks the
 * classic `<script src=".../vs/loader.js">` tag ngx-monaco-editor injects, so
 * the AMD path hangs forever in federation-embedded mode — every editor stays
 * blank and forms bound to it never become valid. Federation chunks, however,
 * DO load (the Native Federation runtime fetches them — `connect-src` — and
 * runs them as `blob:` modules — `script-src blob:`), so a dynamic
 * `import('monaco-editor')` of the ESM build rides that same CSP-safe channel.
 *
 * ngx-monaco-editor's `config.monacoRequire` hook receives this shim instead
 * of an AMD loader: it ignores the module id, imports the bundled monaco, sets
 * the `window.monaco` global the library's editors read, and invokes the AMD
 * callback. Workers: monaco's default ESM worker bootstrap is also broken in
 * the embed — and it HANGS rather than failing (see MONACO_WORKER_ASSETS
 * below), so the shim installs a CSP-safe MonacoEnvironment.getWorker before
 * loading monaco.
 *
 * Styles: esbuild does not auto-load the CSS emitted for a dynamic import
 * (monaco's `editor.main-<hash>.css` chunks are dangling in dist), so the shim
 * links the min build's `editor.main.css` that already ships under
 * `assets/monaco/min/vs` — same package version, resolved through the same
 * absolute base as the AMD path, and allowed by the host CSP
 * (`style-src`/`font-src https://*.ol-hub.com`).
 */
export type MonacoAmdRequire = ((deps: string[], onLoad: () => void) => void) & {
  config: (requireConfig: unknown) => void;
};

/**
 * Language-service workers for the BUNDLED monaco.
 *
 * The bundled ESM build creates its workers with
 * `new Worker(new URL('<label>.worker.js', import.meta.url), { type: 'module' })`.
 * The Angular build does not emit those worker files, and in the embed the
 * document origin is the HOST's — so the module-worker fetch fails
 * asynchronously (cross-origin / SPA-fallback HTML). Monaco's promise-path
 * `WebWorker` has no error→reject wiring for that failure: the proxy `$ping`
 * pends forever, the JS/JSON suggest adapters hang, and because monaco gathers
 * a completion-provider group with `Promise.all`, the hanging adapter
 * suppresses EVERY provider — including the app's own expression completions.
 * That is the "autocomplete does nothing in the platform embed" bug.
 *
 * The fix mirrors what the min build's own bootstrap does (label → worker
 * file), but over the embed's CSP-safe channel: `fetch` the worker script from
 * the remote origin (`connect-src https://*.ol-hub.com` + CORS on the asset
 * bucket) and start it from a `blob:` URL (`worker-src blob:`). The min
 * build's `importScripts`-in-a-blob proxy is NOT usable here — a blob worker
 * inherits the host document's CSP, whose `script-src` excludes the remote
 * origin. The worker files under `min/vs/assets` are self-contained classic
 * IIFE scripts, so the fetched code runs as-is.
 *
 * The hashed filenames are pinned to the exact monaco-editor version in
 * package.json; monaco-embed.spec.ts asserts they match the files the
 * installed package actually ships, so a version bump cannot silently 404.
 *
 * Every failure on this path is a HANG, not an error: monaco attaches no
 * rejection handler to the promise getWorker returns, so a rejected worker
 * leaves the same suppressed-provider state described above. Hence the
 * defensive guards below (an SPA-fallback index.html answers 200) and the loud
 * console.error — this is diagnosability, there is no recovery path once the
 * language adapters exist. `fetch` also makes this the first consumer of
 * `assets/monaco/**` that needs CORS on the asset origin.
 */
export const MONACO_WORKER_ASSETS = {
  editor: 'editor.worker-Be8ye1pW.js',
  json: 'json.worker-DKiEKt88.js',
  css: 'css.worker-HnVq6Ewq.js',
  html: 'html.worker-B51mlPHg.js',
  typescript: 'ts.worker-CMbG-7ft.js',
} as const;

// Same label routing as the min build's own MonacoEnvironment.getWorker.
export function monacoWorkerAssetForLabel(label: string): string {
  switch (label) {
    case 'json':
      return MONACO_WORKER_ASSETS.json;
    case 'css':
    case 'scss':
    case 'less':
      return MONACO_WORKER_ASSETS.css;
    case 'html':
    case 'handlebars':
    case 'razor':
      return MONACO_WORKER_ASSETS.html;
    case 'typescript':
    case 'javascript':
      return MONACO_WORKER_ASSETS.typescript;
    default:
      return MONACO_WORKER_ASSETS.editor;
  }
}

// Keyed by base too: setMonacoVsBase after the first worker would otherwise
// keep serving a blob built from the old origin. The blob URLs are
// deliberately never revoked — they are reused for every editor, and the
// biggest (ts.worker) is ~7MB, so refetching per editor is the worse trade.
const workerBlobUrls = new Map<string, Promise<string>>();

function resolveWorkerBlobUrl(assetFile: string): Promise<string> {
  const base = getMonacoVsBase();
  const cacheKey = `${base}|${assetFile}`;
  const cached = workerBlobUrls.get(cacheKey);
  if (cached) {
    return cached;
  }
  const url = `${base}/assets/${assetFile}`;
  const blobUrl = fetch(url)
    .then((response) => {
      if (!response.ok) {
        throw new Error(`[monaco-embed] worker fetch failed: ${response.status} ${url}`);
      }
      // An SPA fallback answers 200 with index.html; blobbing that yields a
      // worker that dies on parse AFTER this promise resolved — i.e. the hang.
      // Only markup is rejected, never an unexpected-but-valid script MIME.
      if (/text\/html/i.test(response.headers.get('content-type') ?? '')) {
        throw new Error(`[monaco-embed] worker fetch returned HTML, not script: ${url}`);
      }
      return response.text();
    })
    .then((code) => {
      if (code.trimStart().startsWith('<')) {
        throw new Error(`[monaco-embed] worker fetch returned markup, not script: ${url}`);
      }
      return URL.createObjectURL(new Blob([code], { type: 'text/javascript' }));
    });
  blobUrl.catch((error: unknown) => {
    console.error(
      '[monaco-embed] worker script unavailable — monaco language services will hang and all ' +
        'editor autocomplete stays suppressed until this loads (check CORS on the asset origin)',
      error,
    );
    // Do not cache a failed fetch — the next editor init retries. Guarded so a
    // late rejection cannot evict a newer in-flight entry for the same key.
    if (workerBlobUrls.get(cacheKey) === blobUrl) {
      workerBlobUrls.delete(cacheKey);
    }
  });
  workerBlobUrls.set(cacheKey, blobUrl);
  return blobUrl;
}

export function createBundledMonacoWorker(label: string): Promise<Worker> {
  return resolveWorkerBlobUrl(monacoWorkerAssetForLabel(label)).then(
    (blobUrl) => new Worker(blobUrl, { name: label }),
  );
}

const BUNDLED_WORKER_ENV_MARKER = Symbol.for('monaco-embed.bundled-worker-env');

type MonacoEnvironmentHolder = { MonacoEnvironment?: Record<PropertyKey, unknown> };

/**
 * Both monaco worker factories (the core editor worker and every language
 * service) consult `globalThis.MonacoEnvironment.getWorker` FIRST and accept a
 * `Promise<Worker>` — installing this env covers all of them. Only the AMD min
 * build overwrites the assignment with its own (same-origin) getWorker, and
 * the bundled mode never loads that bootstrap.
 *
 * In the embed `globalThis` is the HOST's, shared with every other federated
 * remote, and a single global cannot serve two of them. Designer still wins —
 * skipping would guarantee its own editors break — but the collision is warned
 * about rather than silent, and re-installing our own env is a no-op.
 */
export function installBundledMonacoWorkerEnvironment(): void {
  const holder = globalThis as MonacoEnvironmentHolder;
  const existing = holder.MonacoEnvironment;
  if (existing?.[BUNDLED_WORKER_ENV_MARKER]) {
    return;
  }
  if (typeof existing?.['getWorker'] === 'function') {
    console.warn(
      '[monaco-embed] overwriting a MonacoEnvironment.getWorker installed by the host or another ' +
        'federated remote — their monaco workers may stop loading',
    );
  }
  holder.MonacoEnvironment = {
    [BUNDLED_WORKER_ENV_MARKER]: true,
    getWorker: (_workerId: string, label: string): Promise<Worker> =>
      createBundledMonacoWorker(label),
  };
}

export const MONACO_STYLES_LINK_ID = 'monaco-embed-styles';

function ensureMonacoStylesLink(): void {
  if (document.getElementById(MONACO_STYLES_LINK_ID)) {
    return;
  }
  const link = document.createElement('link');
  link.id = MONACO_STYLES_LINK_ID;
  link.rel = 'stylesheet';
  link.href = `${getMonacoVsBase()}/editor/editor.main.css`;
  document.head.appendChild(link);
}

export function createBundledMonacoRequire(
  loadMonaco: () => Promise<unknown> = (): Promise<unknown> => import('monaco-editor'),
): MonacoAmdRequire {
  const amdRequire = (_deps: string[], onLoad: () => void): void => {
    ensureMonacoStylesLink();
    installBundledMonacoWorkerEnvironment();
    loadMonaco().then(
      (monacoModule) => {
        (window as unknown as { monaco: unknown }).monaco = monacoModule;
        onLoad();
      },
      (error: unknown) => {
        console.error('[monaco-embed] failed to load the bundled monaco editor', error);
      },
    );
  };
  amdRequire.config = (): void => undefined;
  return amdRequire;
}

/**
 * Root-injector provider for ngx-monaco-editor's config. It must live at the
 * ROOT (app + embed modules), not in a lazy feature's `forRoot`: the kit
 * account shell (and any other dynamically-created host) resolves against the
 * root environment injector, where a HubModule-scoped provider is invisible
 * (NG0201). A factory keeps the read lazy — main.ts sets the absolute base
 * before anything injects the editor.
 *
 * `bundled: true` (embed module only) swaps the AMD `baseUrl` for the
 * `monacoRequire` shim above; the standalone build keeps the proven AMD-assets
 * path (including the min build's own worker handling) untouched.
 */
export function provideMonacoEditorConfig(options?: { bundled?: boolean }): StaticProvider {
  return {
    provide: NGX_MONACO_EDITOR_CONFIG,
    useFactory: (themeSync: MonacoThemeSync): NgxMonacoEditorConfig => ({
      ...(options?.bundled
        ? { monacoRequire: createBundledMonacoRequire() }
        : { baseUrl: getMonacoVsBase() }),
      // Monaco loads lazily with the first editor; in dark mode start it on
      // vs-dark and keep it there (OL-11878) — see MonacoThemeSync.
      onMonacoLoad: () => themeSync.onMonacoLoad(),
    }),
    deps: [MonacoThemeSync],
  };
}

// Standalone (AMD) workers need no handling here: monaco ≥0.53's min build
// installs its own `self.MonacoEnvironment.getWorker` (overwriting anything
// the app assigns) that resolves `vs/assets/*.worker-<hash>.js` through the
// AMD loader's `vs` path config — i.e. through this same base — and wraps the
// URL in a Blob proxy, so cross-origin worker loading already works once the
// base is absolute. The worker files ship inside `min/vs`, which angular.json
// copies. The BUNDLED (embed) build never runs that bootstrap — its workers
// come from installBundledMonacoWorkerEnvironment() above.
